import { Color3, Quaternion, Scene, TransformNode, Vector3 } from '@babylonjs/core';
import { RAPIER, type PhysicsWorld } from '@roadworld/physics';
import type { ChunkManager } from '../world/ChunkManager.js';
import { buildVehicleChassis, buildWheelMesh } from './chassisFactory.js';
import type { GraphEdge, RoadGraph } from './roadGraph.js';
import {
  findCrossChunkContinuation,
  pickNextEdge,
  pursuitTarget,
  wrapPi,
  yawFromQuat,
} from './steering.js';
import type { TrafficContext } from './TrafficManager.js';
import { TrafficTuning } from './tuning.js';

/**
 * A single AI traffic car. Rapier dynamic rigid body, driven by direct impulse/torque
 * (hybrid physics — no wheel controller). Follows a directed road graph: at each edge end,
 * picks a successor; at chunk borders, hands off to the neighbor graph by OSM nodeId.
 */
export interface TrafficCarSpawn {
  /** Unique id (used in mesh naming + intersection bookkeeping). */
  id: number;
  /** World-space spawn position. */
  x: number;
  y: number;
  z: number;
  /** Yaw in radians (0 = +Z). */
  yaw: number;
  /** Target forward speed in m/s. */
  targetSpeedMs: number;
  /** Visual chassis color. */
  bodyColor: Color3;
  /** The graph this car starts on. */
  graph: RoadGraph;
  /** The edge this car starts on. */
  edgeId: number;
  /** Initial progress along that edge (0..1). */
  tParam: number;
}

export class TrafficCar {
  readonly id: number;
  private physics: PhysicsWorld;
  private chunks: ChunkManager;
  private body: RAPIER.RigidBody;
  private collider: RAPIER.Collider;
  private root: TransformNode;
  private wheels: TransformNode[] = [];

  /** Target forward speed for this car's class (m/s). */
  classTargetMs: number;
  /** Yield slowdown (m/s). Math.min vs classTargetMs gives effective. Slice 5 will wire. */
  yieldTargetMs = Infinity;
  /** Obstacle slowdown (m/s). Slice 6 wires this. */
  obstacleTargetMs = Infinity;

  // --- Path state ---
  private graph: RoadGraph;
  private edgeId: number;
  /** Progress along current edge, 0..1. Bumped each tick by speed*dt/length. */
  private tParam: number;
  /** RNG for next-edge picks. Slice 1 used Math.random; pass-in keeps determinism options open. */
  private rng: () => number;

  /** OSM nodeId of the intersection this car currently holds a reservation on, if any.
   *  Cleared when the car commits past the intersection (picks next edge whose fromNode
   *  matches this id). */
  private heldIntersection: number | null = null;

  /** Session-time threshold at which to next run the obstacle raycast. Initialized to 0 so
   *  the first tick always casts; subsequent ticks rate-limit to obstacleRayHz. */
  private nextRayCheckSec = 0;
  /** Time (sec) the car has been simultaneously blocked AND nearly stopped. Resets when
   *  the obstacle clears or the car gets moving again. */
  private blockedSeconds = 0;
  /** Time (sec) the car has been off-road (surface = grass). Resets to 0 on road/path. */
  private offRoadSeconds = 0;

  /** Set true to drop this car from the manager on the next tick. */
  shouldDespawn = false;
  private disposed = false;
  /** Current wheel visibility — tracked so updateVisualLOD only touches the scene graph
   *  when the band changes. */
  private wheelsVisible = true;

  constructor(
    physics: PhysicsWorld,
    scene: Scene,
    chunks: ChunkManager,
    spawn: TrafficCarSpawn,
    rng: () => number = Math.random,
  ) {
    this.physics = physics;
    this.chunks = chunks;
    this.id = spawn.id;
    this.classTargetMs = spawn.targetSpeedMs;
    this.graph = spawn.graph;
    this.edgeId = spawn.edgeId;
    this.tParam = spawn.tParam;
    this.rng = rng;

    const T = TrafficTuning;

    // --- Rigid body + collider ---
    const bodyDesc = RAPIER.RigidBodyDesc.dynamic()
      .setTranslation(spawn.x, spawn.y, spawn.z)
      .setRotation(yawToQuat(spawn.yaw))
      .setCcdEnabled(true)
      .setLinearDamping(T.linearDamping)
      .setAngularDamping(T.angularDamping);
    this.body = physics.world.createRigidBody(bodyDesc);

    const colliderDesc = RAPIER.ColliderDesc.cuboid(
      T.chassisWidth * 0.5,
      T.chassisHeight * 0.5,
      T.chassisLength * 0.5,
    )
      .setDensity(0)
      .setFriction(T.friction)
      .setRestitution(T.restitution);
    this.collider = physics.world.createCollider(colliderDesc, this.body);

    // Mass + inertia. Low CoM resists collision-induced flipping.
    const w = T.chassisWidth;
    const h = T.chassisHeight;
    const d = T.chassisLength;
    const inertiaX = (T.chassisMass / 12) * (h * h + d * d);
    const inertiaY = (T.chassisMass / 12) * (w * w + d * d);
    const inertiaZ = (T.chassisMass / 12) * (w * w + h * h);
    this.body.setAdditionalMassProperties(
      T.chassisMass,
      { x: 0, y: -0.3, z: 0 },
      { x: inertiaX, y: inertiaY, z: inertiaZ },
      { x: 0, y: 0, z: 0, w: 1 },
      true,
    );

    // --- Visuals ---
    this.root = new TransformNode(`traffic-${spawn.id}`, scene);
    const chassis = buildVehicleChassis(scene, T, spawn.bodyColor, String(spawn.id));
    chassis.parent = this.root;

    const wheelTrack = w * 0.47;
    const wheelOffsetZ = d * 0.37;
    const wheelY = -h * 0.25;
    const corners: Array<{ x: number; z: number }> = [
      { x: wheelTrack, z: wheelOffsetZ },
      { x: -wheelTrack, z: wheelOffsetZ },
      { x: wheelTrack, z: -wheelOffsetZ },
      { x: -wheelTrack, z: -wheelOffsetZ },
    ];
    for (let i = 0; i < corners.length; i++) {
      const c = corners[i]!;
      const wheel = buildWheelMesh(scene, T, `wheel-${spawn.id}-${i}`);
      wheel.parent = this.root;
      wheel.position.set(c.x, wheelY, c.z);
      this.wheels.push(wheel);
    }
  }

  /** Step one frame. Called by TrafficManager after physics.step. */
  update(dt: number, ctx: TrafficContext): void {
    if (this.disposed || this.shouldDespawn) return;
    const T = TrafficTuning;

    // 1) Advance path progress based on actual forward speed.
    const linvel = this.body.linvel();
    const rot = this.body.rotation();
    const yaw = yawFromQuat(rot);
    const fwd = { x: Math.sin(yaw), z: Math.cos(yaw) };
    const fwdSpeed = linvel.x * fwd.x + linvel.z * fwd.z;
    const advanceDist = Math.max(0, fwdSpeed) * dt;

    const currentEdge = this.graph.edges[this.edgeId];
    if (!currentEdge) {
      // Graph was disposed under us (chunk unloaded). Release any held intersection
      // before vanishing so we don't block other cars.
      if (this.heldIntersection !== null) ctx.release(this.id, this.heldIntersection);
      this.shouldDespawn = true;
      return;
    }

    this.tParam += advanceDist / Math.max(currentEdge.length, 1e-3);
    while (this.tParam >= 1) {
      const overshoot = (this.tParam - 1) * currentEdge.length;
      const finishedEdge = this.graph.edges[this.edgeId]!;

      // Commit-past-intersection: if we just crossed the node we were holding, release it.
      if (this.heldIntersection !== null && this.heldIntersection === finishedEdge.toNode) {
        ctx.release(this.id, this.heldIntersection);
        this.heldIntersection = null;
      }

      // We just reached the end of currentEdge. Pick the next edge.
      const next = this.pickContinuation();
      if (!next) {
        // No continuation available (neighbor chunk not loaded). Snap to edge end and
        // crawl until something becomes available; if the car keeps trying to leave,
        // the off-road / blocked despawn (slice 6) will reap it.
        this.tParam = 1;
        break;
      }
      this.graph = next.graph;
      this.edgeId = next.edgeId;
      this.tParam = overshoot / Math.max(this.graph.edges[this.edgeId]!.length, 1e-3);
    }

    const liveEdge = this.graph.edges[this.edgeId]!;

    // 2) Intersection yield. If the end node of the current edge is an intersection (degree>=3)
    //    and we're within lookahead distance, attempt to reserve. First-arriving car wins;
    //    others slow to a crawl (or stop very close to the intersection). The reservation
    //    is held until the car crosses the node, with a 2s timeout as a deadlock release.
    this.yieldTargetMs = Infinity;
    const endNode = this.graph.nodes.get(liveEdge.toNode);
    if (endNode?.isIntersection) {
      // Distance from current position along the edge to the end node.
      const distToEnd = (1 - this.tParam) * liveEdge.length;
      if (distToEnd < T.intersectionLookaheadM) {
        const verdict = ctx.reserve(this.id, endNode.id);
        if (verdict === 'go') {
          this.heldIntersection = endNode.id;
        } else {
          // Someone else has it. Crawl, or stop entirely if very close.
          this.yieldTargetMs =
            distToEnd < T.intersectionStopDistM ? 0 : T.yieldCrawlSpeedMs;
        }
      }
    }

    // 3) Pursuit target — walk ~lookahead meters along the path, applying lane offset
    //    at the final point. If the path runs out (no continuation), use end of edge.
    const target = pursuitTarget(liveEdge, this.tParam, T.lookaheadMeters, (e) => {
      // PEEK at the next edge for pursuit math only — don't actually commit. Picking
      // here would couple steering to commitment which makes intersections jittery.
      const peek = pickNextEdge(this.graph, e.id, this.rng);
      return peek;
    });

    // 4) Steering: turn toward the target via Y-axis torque, clamped against MAX_YAW_RATE.
    let yawErr = 0;
    if (target) {
      const pos = this.body.translation();
      const desiredYaw = Math.atan2(target.x - pos.x, target.z - pos.z);
      yawErr = wrapPi(desiredYaw - yaw);
    }
    const desiredAv = clamp(yawErr * T.turnGain, -T.maxYawRateRadS, T.maxYawRateRadS);
    const av = this.body.angvel();
    const avErr = desiredAv - av.y;
    // Torque impulse: F·dt-equivalent, scaled by yawInertiaScale and mass.
    this.body.applyTorqueImpulse(
      { x: 0, y: avErr * T.yawInertiaScale * dt, z: 0 },
      true,
    );

    // 5a) Obstacle awareness via forward raycast. Rate-limited to obstacleRayHz so 30 cars
    //     don't fire 30 raycasts per substep. The filterExcludeCollider arg keeps the cast
    //     from hitting our own chassis.
    if (ctx.nowSec >= this.nextRayCheckSec) {
      this.nextRayCheckSec = ctx.nowSec + 1 / T.obstacleRayHz;
      const pos = this.body.translation();
      const ray = new RAPIER.Ray(
        { x: pos.x, y: pos.y + 0.4, z: pos.z },
        { x: fwd.x, y: 0, z: fwd.z },
      );
      const hit = this.physics.world.castRay(
        ray,
        T.obstacleRayLengthM,
        true,
        undefined,
        undefined,
        this.collider,
      );
      if (hit && hit.timeOfImpact < T.obstacleSlowdownDistM) {
        this.obstacleTargetMs = hit.timeOfImpact < T.obstacleStopDistM ? 0 : T.yieldCrawlSpeedMs;
      } else {
        this.obstacleTargetMs = Infinity;
      }
    }

    // 5b) Blocked-too-long despawn. "Blocked" = obstacle slowdown active AND actually stopped
    //     (speed < 0.5 m/s). Without the speed check, a car that's just rolling slowly behind
    //     another car would falsely accumulate blocked time and despawn.
    const isBlockedNow = this.obstacleTargetMs !== Infinity && Math.abs(fwdSpeed) < 0.5;
    if (isBlockedNow) {
      this.blockedSeconds += dt;
      if (this.blockedSeconds > T.blockedDespawnSec) {
        if (this.heldIntersection !== null) ctx.release(this.id, this.heldIntersection);
        this.shouldDespawn = true;
        return;
      }
    } else {
      this.blockedSeconds = 0;
    }

    // 5c) Off-road despawn. If the car has wandered onto grass (collision shoved it, etc.)
    //     and stayed there for offRoadDespawnSec, give up and reap. Surface lookup returns
    //     null while the chunk is still loading — treat that as "unknown, don't accumulate".
    const pos = this.body.translation();
    const surface = this.chunks.classifySurface(pos.x, pos.z);
    if (surface === 'grass') {
      this.offRoadSeconds += dt;
      if (this.offRoadSeconds > T.offRoadDespawnSec) {
        if (this.heldIntersection !== null) ctx.release(this.id, this.heldIntersection);
        this.shouldDespawn = true;
        return;
      }
    } else {
      this.offRoadSeconds = 0;
    }

    // 5) Forward thrust toward effective target speed. If pursuit failed (no continuation),
    //    fall back to a crawl so the car doesn't blast across an unloaded chunk border.
    const effective = target
      ? Math.min(this.classTargetMs, this.yieldTargetMs, this.obstacleTargetMs)
      : Math.min(2.0, this.classTargetMs); // crawl when path is blocked
    const speedErr = effective - fwdSpeed;
    const rate = speedErr >= 0 ? T.thrustImpulseMaxRate : T.brakeImpulseMaxRate;
    const impulseRate = clamp(speedErr * T.speedGain, -rate, rate);
    const impulse = impulseRate * dt;
    this.body.applyImpulse({ x: fwd.x * impulse, y: 0, z: fwd.z * impulse }, true);

    // 6) Lateral velocity kill — replaces wheel side friction. Without it, AI cars slide
    //    outward in turns like a curling stone.
    const rightX = 1 - 2 * (rot.y * rot.y + rot.z * rot.z);
    const rightZ = 2 * (rot.x * rot.z - rot.y * rot.w);
    const lateral = linvel.x * rightX + linvel.z * rightZ;
    if (Math.abs(lateral) > 0.01) {
      const damp = lateral * T.lateralDamp * T.chassisMass * dt;
      this.body.applyImpulse({ x: -rightX * damp, y: 0, z: -rightZ * damp }, true);
    }
  }

  /**
   * Choose the next edge after the car finishes the current edge. Tries same-graph
   * successors first; if the end node has no outgoing edges in this graph (chunk border),
   * looks up the same OSM nodeId in the neighbor chunk's graph and hands off.
   */
  private pickContinuation(): { graph: RoadGraph; edgeId: number } | null {
    const currentEdge = this.graph.edges[this.edgeId];
    if (!currentEdge) return null;

    // Same-graph pick first.
    const sameGraph = pickNextEdge(this.graph, this.edgeId, this.rng);
    if (sameGraph) {
      return { graph: this.graph, edgeId: sameGraph.id };
    }

    // Try crossing to a neighbor chunk via the OSM nodeId of the current edge's end node.
    const endNode = this.graph.nodes.get(currentEdge.toNode);
    if (!endNode) return null;
    const handoff = findCrossChunkContinuation(this.chunks, endNode.x, endNode.z, endNode.id);
    if (!handoff) return null;

    // In the neighbor graph, pick the highest-weighted out-edge from the same nodeId.
    const neighborNode = handoff.graph.nodes.get(handoff.nodeId);
    if (!neighborNode || neighborNode.outEdges.length === 0) return null;
    // Reuse pickNextEdge by faking a "from" edge that points INTO this node along the same
    // tangent — easiest is to find ANY outgoing and roll from there. Simpler: just emulate
    // the same weighted pick directly.
    let totalW = 0;
    for (const eid of neighborNode.outEdges) {
      const e = handoff.graph.edges[eid];
      if (e) totalW += weightOf(e);
    }
    if (totalW <= 0) return null;
    let roll = this.rng() * totalW;
    for (const eid of neighborNode.outEdges) {
      const e = handoff.graph.edges[eid];
      if (!e) continue;
      roll -= weightOf(e);
      if (roll <= 0) return { graph: handoff.graph, edgeId: eid };
    }
    return { graph: handoff.graph, edgeId: neighborNode.outEdges[0]! };
  }

  /** Push physics state to visual. */
  syncVisual(): void {
    if (this.disposed) return;
    const t = this.body.translation();
    const q = this.body.rotation();
    this.root.position.set(t.x, t.y, t.z);
    const rotQ = this.root.rotationQuaternion ?? (this.root.rotationQuaternion = Quaternion.Identity());
    rotQ.set(q.x, q.y, q.z, q.w);
  }

  /** Distance-based wheel LOD. Past ~60 m the wheels become a few pixels each and dominate
   *  the per-car draw call count for no visual benefit. When `lodEnabled` is false, wheels
   *  are always visible regardless of distance (used by the LOD-off A/B comparison). The
   *  chassis stays drawn at every distance — culling it would make distant cars vanish into
   *  flat color, which reads as a bug. */
  updateVisualLOD(camX: number, camZ: number, lodEnabled: boolean): void {
    if (this.disposed) return;
    const t = this.body.translation();
    const dx = t.x - camX;
    const dz = t.z - camZ;
    const distSq = dx * dx + dz * dz;
    const showWheels = !lodEnabled || distSq < TRAFFIC_WHEEL_LOD_DISTANCE_SQ;
    if (showWheels === this.wheelsVisible) return;
    this.wheelsVisible = showWheels;
    for (const w of this.wheels) w.setEnabled(showWheels);
  }

  position(): Vector3 {
    const t = this.body.translation();
    return new Vector3(t.x, t.y, t.z);
  }

  getYaw(): number {
    return yawFromQuat(this.body.rotation());
  }

  speed(): number {
    const v = this.body.linvel();
    return Math.hypot(v.x, v.z);
  }

  getCollider(): RAPIER.Collider {
    return this.collider;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.root.dispose(false, true);
    for (const w of this.wheels) w.dispose(false, true);
    this.physics.world.removeRigidBody(this.body);
  }
}

/** Squared distance (m²) beyond which a car's 4 wheel meshes are hidden. 60 m — past this
 *  wheels are well under a pixel each on the screen, so dropping them saves 4 draw calls
 *  per car at zero visual cost. */
const TRAFFIC_WHEEL_LOD_DISTANCE_SQ = 60 * 60;

function yawToQuat(yaw: number): RAPIER.Rotation {
  const half = yaw * 0.5;
  return { x: 0, y: Math.sin(half), z: 0, w: Math.cos(half) };
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Weight an edge for picking, identical to the rule in steering.pickNextEdge. */
function weightOf(edge: GraphEdge): number {
  return TrafficTuning.spawnWeight[edge.roadClass] ?? TrafficTuning.defaultSpawnWeight;
}
